import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Users, Settings, BarChart3, UserCheck, Trash2, LogOut, Music, Edit, Power, PowerOff, Download, Upload, RefreshCw, Database, Shield, AlertTriangle, Crown, Star, Calendar, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";

interface Host {
  id: string;
  username: string;
  display_name: string;
  platform_id: string;
  created_at: string;
  venue_name?: string;
  venue_id?: string;
  is_active?: boolean;
  password_hash?: string;
  subscription_level?: string;
  subscription_start_date?: string;
  subscription_duration_months?: number;
  subscription_end_date?: string;
}

const SuperAdmin = () => {
  const { toast } = useToast();
  const navigate = useNavigate();

  // Check authentication
  useEffect(() => {
    const isAuthenticated = localStorage.getItem('superAdminAuth') === 'true';
    if (!isAuthenticated) {
      navigate('/cook');
    }
  }, [navigate]);
  const [hosts, setHosts] = useState<Host[]>([]);
  const [venues, setVenues] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newHost, setNewHost] = useState({ username: '', display_name: '', password: '', venue_name: '', subscription_level: 'basic', subscription_duration_months: 12 });
  const [totalRequests, setTotalRequests] = useState(0);
  const [totalSongs, setTotalSongs] = useState(0);
  const [editingHost, setEditingHost] = useState<Host | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);

  // Load data from database
  useEffect(() => {
    loadHosts();
    loadVenues();
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    try {
      // Count total requests (all statuses)
      const { count: requestCount, error: requestError } = await supabase
        .from('requests')
        .select('*', { count: 'exact', head: true });

      if (requestError) throw requestError;
      setTotalRequests(requestCount || 0);

      // Count total songs across all venues
      const { count: songCount, error: songError } = await supabase
        .from('songs')
        .select('*', { count: 'exact', head: true });

      if (songError) throw songError;
      setTotalSongs(songCount || 0);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const loadHosts = async () => {
    try {
      const { data, error } = await supabase
        .from('hosts')
        .select(`
          id,
          username,
          display_name,
          platform_id,
          created_at,
          password_hash,
          is_active,
          subscription_level,
          subscription_start_date,
          subscription_duration_months,
          subscription_end_date,
          venue_hosts (
            venue_id,
            venues (
              id,
              name
            )
          )
        `);

      if (error) throw error;

      const formattedHosts = data?.map(host => ({
        id: host.id,
        username: host.username,
        display_name: host.display_name,
        platform_id: host.platform_id,
        created_at: host.created_at,
        subscription_level: host.subscription_level,
        subscription_start_date: host.subscription_start_date,
        subscription_duration_months: host.subscription_duration_months,
        subscription_end_date: host.subscription_end_date,
        venue_name: host.venue_hosts?.[0]?.venues?.name || 'No venue assigned',
        venue_id: host.venue_hosts?.[0]?.venues?.id || ''
      })) || [];

      setHosts(formattedHosts);
    } catch (error) {
      console.error('Error loading hosts:', error);
      toast({
        title: "Error",
        description: "Failed to load hosts",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadVenues = async () => {
    try {
      const { data, error } = await supabase
        .from('venues')
        .select('id, name, slug');

      if (error) throw error;
      setVenues(data || []);
    } catch (error) {
      console.error('Error loading venues:', error);
    }
  };

  const handleCreateHost = async () => {
    if (!newHost.username || !newHost.display_name || !newHost.password || !newHost.venue_name) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    try {
      // Get platform ID (using the first available platform or create default)
      let platformData;
      const { data: existingPlatform, error: platformError } = await supabase
        .from('platforms')
        .select('id')
        .limit(1)
        .single();

      if (platformError || !existingPlatform) {
        // Create default platform — never store raw credentials in DB
        const { data: newPlatform, error: createError } = await supabase
          .from('platforms')
          .insert({
            name: 'KaraokeCore Platform',
            super_admin_email: '',
            super_admin_password_hash: ''
          })
          .select()
          .single();

        if (createError) throw createError;
        platformData = newPlatform;
      } else {
        platformData = existingPlatform;
      }

      // Create or get venue
      let venueId = null;
      const { data: existingVenues, error: venueCheckError } = await supabase
        .from('venues')
        .select('id')
        .eq('name', newHost.venue_name);

      if (venueCheckError) {
        console.error('Error checking venue:', venueCheckError);
        throw venueCheckError;
      }

      if (existingVenues && existingVenues.length > 0) {
        venueId = existingVenues[0].id;
      } else {
        // Create new venue
        const { data: newVenue, error: venueCreateError } = await supabase
          .from('venues')
          .insert([
            {
              name: newHost.venue_name,
              slug: newHost.venue_name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
              platform_id: platformData.id,
              welcome_message: `Welcome to ${newHost.venue_name}!`,
              primary_color: '#10b981',
              secondary_color: '#3b82f6'
            }
          ])
          .select()
          .single();

        if (venueCreateError) throw venueCreateError;
        venueId = newVenue.id;
      }

      // Hash the password SERVER-SIDE (bcrypt) — never hash on the client.
      const { data: pwHash, error: hashErr } = await supabase.rpc('admin_hash_password', { p_password: newHost.password });
      if (hashErr || !pwHash) throw hashErr || new Error('Could not hash password');

      // Create host — store the bcrypt hash, never the raw password
      const { data: hostData, error: hostError } = await supabase
        .from('hosts')
        .insert([
          {
            username: newHost.username,
            display_name: newHost.display_name,
            password_hash: pwHash as string,
            platform_id: platformData.id,
            subscription_level: newHost.subscription_level,
            subscription_duration_months: newHost.subscription_duration_months
          }
        ])
        .select()
        .single();

      if (hostError) throw hostError;

      // Link host to venue
      const { error: linkError } = await supabase
        .from('venue_hosts')
        .insert([
          {
            host_id: hostData.id,
            venue_id: venueId
          }
        ]);

      if (linkError) throw linkError;

      // Reload hosts and statistics to show the new one
      await loadHosts();
      await loadStatistics();

      setNewHost({ username: '', display_name: '', password: '', venue_name: '', subscription_level: 'basic', subscription_duration_months: 12 });
      setShowCreateForm(false);

      toast({
        title: "Success",
        description: "Host created successfully",
      });
    } catch (error) {
      console.error('Error creating host:', error);
      toast({
        title: "Error",
        description: "Failed to create host",
        variant: "destructive",
      });
    }
  };

  const handleEditHost = (host: Host) => {
    setEditingHost(host);
    setShowEditForm(true);
  };

  const handleUpdateHost = async () => {
    if (!editingHost) return;

    try {
      // Only re-hash the password if it has been changed (non-empty and not already a 64-char hex hash)
      const updatePayload: Record<string, string> = {
        username: editingHost.username,
        display_name: editingHost.display_name,
      };
      const pw = editingHost.password_hash;
      // Already-hashed values (legacy SHA-256 hex, or bcrypt) are left as-is;
      // anything else is a new plaintext password the admin typed → hash it
      // server-side with bcrypt.
      const looksHashed = !!pw && (/^[a-f0-9]{64}$/.test(pw) || /^\$2[aby]\$/.test(pw));
      if (pw && !looksHashed) {
        const { data: pwHash, error: hashErr } = await supabase.rpc('admin_hash_password', { p_password: pw });
        if (hashErr || !pwHash) throw hashErr || new Error('Could not hash password');
        updatePayload.password_hash = pwHash as string;
      } else if (pw) {
        updatePayload.password_hash = pw;
      }

      const { error } = await supabase
        .from('hosts')
        .update(updatePayload)
        .eq('id', editingHost.id);

      if (error) throw error;

      await loadHosts();
      setShowEditForm(false);
      setEditingHost(null);

      toast({
        title: "Host Updated",
        description: "Host details have been updated successfully",
      });
    } catch (error) {
      console.error('Error updating host:', error);
      toast({
        title: "Error",
        description: "Failed to update host",
        variant: "destructive",
      });
    }
  };

  const handleExtendSubscription = async (hostId: string, additionalMonths: number) => {
    try {
      const { error } = await supabase.rpc('extend_subscription', {
        host_id: hostId,
        additional_months: additionalMonths
      });

      if (error) throw error;

      await loadHosts();

      toast({
        title: "Subscription Extended",
        description: `Subscription extended by ${additionalMonths} months`,
      });
    } catch (error) {
      console.error('Error extending subscription:', error);
      toast({
        title: "Error",
        description: "Failed to extend subscription",
        variant: "destructive",
      });
    }
  };

  const handleUpdateSubscriptionLevel = async (hostId: string, newLevel: string) => {
    try {
      const { error } = await supabase
        .from('hosts')
        .update({ subscription_level: newLevel })
        .eq('id', hostId);

      if (error) throw error;

      await loadHosts();

      toast({
        title: "Subscription Updated",
        description: `Host subscription changed to ${newLevel}`,
      });
    } catch (error) {
      console.error('Error updating subscription level:', error);
      toast({
        title: "Error",
        description: "Failed to update subscription level",
        variant: "destructive",
      });
    }
  };

  const handleToggleSubscriptionLevel = async (hostId: string, currentLevel: string) => {
    try {
      // Valid tiers: 'basic' | 'regular' | 'enterprise'
      const newLevel = currentLevel === 'enterprise' ? 'regular' : 'enterprise';
      const { error } = await supabase
        .from('hosts')
        .update({ subscription_level: newLevel })
        .eq('id', hostId);

      if (error) throw error;

      await loadHosts();

      toast({
        title: `Subscription ${newLevel === 'enterprise' ? 'Upgraded to Enterprise' : 'Set to Regular'}`,
        description: `Host subscription changed to ${newLevel}`,
      });
    } catch (error) {
      console.error('Error changing subscription level:', error);
      toast({
        title: "Error",
        description: "Failed to update subscription level",
        variant: "destructive",
      });
    }
  };

  const handleToggleHostActivation = async (hostId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('hosts')
        .update({ is_active: !currentStatus })
        .eq('id', hostId);

      if (error) throw error;

      await loadHosts();

      toast({
        title: currentStatus ? "Host Deactivated" : "Host Activated",
        description: currentStatus
          ? "Host subscription has been suspended"
          : "Host subscription has been restored",
      });
    } catch (error) {
      console.error('Error toggling host activation:', error);
      toast({
        title: "Error",
        description: "Failed to update host status",
        variant: "destructive",
      });
    }
  };

  const handleBulkActivateHosts = async () => {
    try {
      const { error } = await supabase
        .from('hosts')
        .update({ is_active: true })
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Update all

      if (error) throw error;

      await loadHosts();
      toast({
        title: "Bulk Activation Complete",
        description: "All hosts have been activated",
      });
    } catch (error) {
      console.error('Error in bulk activation:', error);
      toast({
        title: "Error",
        description: "Failed to activate all hosts",
        variant: "destructive",
      });
    }
  };

  const handleBulkDeactivateHosts = async () => {
    try {
      const { error } = await supabase
        .from('hosts')
        .update({ is_active: false })
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Update all

      if (error) throw error;

      await loadHosts();
      toast({
        title: "Bulk Deactivation Complete",
        description: "All hosts have been deactivated",
      });
    } catch (error) {
      console.error('Error in bulk deactivation:', error);
      toast({
        title: "Error",
        description: "Failed to deactivate all hosts",
        variant: "destructive",
      });
    }
  };

  const handleExportData = async () => {
    try {
      const { data: hostsData } = await supabase.from('hosts').select('*');
      const { data: venuesData } = await supabase.from('venues').select('*');
      const { data: requestsData } = await supabase.from('requests').select('*');

      const exportData = {
        hosts: hostsData,
        venues: venuesData,
        requests: requestsData,
        exported_at: new Date().toISOString()
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `karaoke-data-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);

      toast({
        title: "Data Exported",
        description: "System data has been exported successfully",
      });
    } catch (error) {
      console.error('Error exporting data:', error);
      toast({
        title: "Export Failed",
        description: "Failed to export system data",
        variant: "destructive",
      });
    }
  };

  const handleDeleteHost = async (hostId: string) => {
    if (confirm('Are you sure you want to delete this host? This action cannot be undone.')) {
      try {
        // Get host's venue information before deletion
        const { data: hostData } = await supabase
          .from('venue_hosts')
          .select('venue_id, venues(name)')
          .eq('host_id', hostId)
          .single();

        const { error } = await supabase
          .from('hosts')
          .delete()
          .eq('id', hostId);

        if (error) throw error;

        // Reload hosts and statistics to reflect the deletion
        await loadHosts();
        await loadStatistics();
        await loadVenues();

        // Ask if admin wants to delete the associated venue
        if (hostData?.venue_id) {
          const deleteVenue = confirm(
            `Host deleted successfully. Do you also want to delete the associated venue "${hostData.venues?.name}"? This will remove all songs and requests for this venue.`
          );

          if (deleteVenue) {
            await handleDeleteVenue(hostData.venue_id);
          }
        }

        toast({
          title: "Host Deleted",
          description: "Host account has been removed",
        });
      } catch (error) {
        console.error('Error deleting host:', error);
        toast({
          title: "Error",
          description: "Failed to delete host",
          variant: "destructive",
        });
      }
    }
  };

  const handleResetSongDatabase = async (venueId: string, venueName: string) => {
    const confirmReset = confirm(
      `Are you sure you want to reset the song database for "${venueName}"?\n\n` +
      `This will permanently delete ALL songs for this venue. This action cannot be undone.\n\n` +
      `Type "RESET" to confirm this action.`
    );

    if (!confirmReset) return;

    const userInput = prompt('Type "RESET" to confirm:');
    if (userInput !== 'RESET') {
      toast({
        title: "Reset Cancelled",
        description: "Song database reset was cancelled",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('songs')
        .delete()
        .eq('venue_id', venueId);

      if (error) throw error;

      toast({
        title: "Song Database Reset",
        description: `All songs for "${venueName}" have been deleted`,
      });

      await loadStatistics();
    } catch (error) {
      console.error('Error resetting song database:', error);
      toast({
        title: "Error",
        description: "Failed to reset song database",
        variant: "destructive",
      });
    }
  };

  const handleDeleteVenue = async (venueId: string) => {
    try {
      // Check if there are active hosts linked to this venue
      const { data: activeHosts, error: hostError } = await supabase
        .from('venue_hosts')
        .select('host_id, hosts(username, is_active)')
        .eq('venue_id', venueId);

      if (hostError) throw hostError;

      const activeHostsList = activeHosts?.filter(vh => vh.hosts?.is_active !== false) || [];

      if (activeHostsList.length > 0) {
        const hostNames = activeHostsList.map(vh => vh.hosts?.username).join(', ');

        const shouldUnbind = confirm(
          `This venue has ${activeHostsList.length} active host(s): ${hostNames}.\n\n` +
          `You must first deactivate or delete these hosts before deleting the venue.\n\n` +
          `Would you like to deactivate these hosts now?`
        );

        if (shouldUnbind) {
          // Deactivate all hosts for this venue
          const hostIds = activeHostsList.map(vh => vh.host_id);
          const { error: deactivateError } = await supabase
            .from('hosts')
            .update({ is_active: false })
            .in('id', hostIds);

          if (deactivateError) throw deactivateError;

          toast({
            title: "Hosts Deactivated",
            description: `${activeHostsList.length} host(s) have been deactivated. You can now delete the venue.`,
          });

          await loadHosts();
          return; // Don't delete venue yet, let admin confirm again
        } else {
          return; // User cancelled
        }
      }

      // Proceed with venue deletion if no active hosts
      const confirmDelete = confirm(
        'Are you sure you want to delete this venue? This will remove all songs, requests, and customizations. This action cannot be undone.'
      );

      if (!confirmDelete) return;

      const { error } = await supabase
        .from('venues')
        .delete()
        .eq('id', venueId);

      if (error) throw error;

      await loadVenues();
      await loadStatistics();

      toast({
        title: "Venue Deleted",
        description: "Venue and all associated data has been deleted",
      });
    } catch (error) {
      console.error('Error deleting venue:', error);
      toast({
        title: "Error",
        description: "Failed to delete venue",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen stage-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6 lg:space-y-8">
        <AppHeader
          title="KaraokeCore SuperAdmin"
          subtitle="Manage the karaoke empire"
          showHomeButton={true}
          rightContent={
            <Button
              variant="outline"
              className="text-destructive"
              onClick={() => {
                localStorage.removeItem('superAdminAuth');
                localStorage.removeItem('userRole');
                navigate("/");
              }}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </Button>
          }
        />

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          <Card className="card-neon hover:scale-105 transition-transform duration-200">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center space-x-3 sm:space-x-4">
                <div className="p-2 sm:p-3 bg-primary/20 rounded-full">
                  <Users className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-primary-glow">{hosts.length}</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">Total Hosts</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-neon hover:scale-105 transition-transform duration-200">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center space-x-3 sm:space-x-4">
                <div className="p-2 sm:p-3 bg-karaoke-queue/20 rounded-full">
                  <UserCheck className="h-6 w-6 sm:h-8 sm:w-8 text-karaoke-queue" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-karaoke-queue">{hosts.filter(h => h.status === 'active').length}</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">Active Venues</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-neon hover:scale-105 transition-transform duration-200">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center space-x-3 sm:space-x-4">
                <div className="p-2 sm:p-3 bg-karaoke-neon-pink/20 rounded-full">
                  <BarChart3 className="h-6 w-6 sm:h-8 sm:w-8 text-karaoke-neon-pink" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-karaoke-neon-pink">{totalRequests.toLocaleString()}</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">Total Requests</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-neon hover:scale-105 transition-transform duration-200">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center space-x-3 sm:space-x-4">
                <div className="p-2 sm:p-3 bg-primary/20 rounded-full">
                  <Music className="h-6 w-6 sm:h-8 sm:w-8 text-primary-glow" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-primary-glow">{totalSongs.toLocaleString()}</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">Total Songs</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-neon hover:scale-105 transition-transform duration-200">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center space-x-3 sm:space-x-4">
                <div className="p-2 sm:p-3 bg-green-500/20 rounded-full">
                  <Settings className="h-6 w-6 sm:h-8 sm:w-8 text-green-400" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-green-400">
                    {loading ? "..." : "Operational"}
                  </p>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {hosts.length} hosts • {venues.length} venues
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* System Controls */}
        <Card className="card-neon">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Settings className="h-5 w-5 text-primary" />
              System Controls
            </CardTitle>
            <CardDescription>Bulk operations and system management</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Button
                onClick={handleBulkActivateHosts}
                variant="outline"
                className="flex flex-col gap-2 h-20 hover:bg-green-500/10 hover:border-green-500/50 transition-colors"
              >
                <Power className="h-5 w-5 text-green-500" />
                <span className="text-xs font-medium">Activate All</span>
              </Button>
              <Button
                onClick={handleBulkDeactivateHosts}
                variant="outline"
                className="flex flex-col gap-2 h-20 hover:bg-red-500/10 hover:border-red-500/50 transition-colors"
              >
                <PowerOff className="h-5 w-5 text-red-500" />
                <span className="text-xs font-medium">Deactivate All</span>
              </Button>
              <Button
                onClick={handleExportData}
                variant="outline"
                className="flex flex-col gap-2 h-20 hover:bg-blue-500/10 hover:border-blue-500/50 transition-colors"
              >
                <Download className="h-5 w-5 text-blue-500" />
                <span className="text-xs font-medium">Export Data</span>
              </Button>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="flex flex-col gap-2 h-20 hover:bg-purple-500/10 hover:border-purple-500/50 transition-colors"
              >
                <RefreshCw className="h-5 w-5 text-purple-500" />
                <span className="text-xs font-medium">Refresh</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Host Management */}
        <Card className="card-neon">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-2xl">Host Management</CardTitle>
                <CardDescription>Create and manage Host/KJ accounts</CardDescription>
              </div>
              <Button 
                onClick={() => setShowCreateForm(!showCreateForm)}
                className="btn-karaoke"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create Host
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Create Form */}
            {showCreateForm && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4 sm:p-6 border border-primary/20 rounded-lg bg-muted/10">
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    value={newHost.username}
                    onChange={(e) => setNewHost({...newHost, username: e.target.value})}
                    placeholder="host_username"
                    className="input-glow"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="display_name">Display Name</Label>
                  <Input
                    id="display_name"
                    value={newHost.display_name}
                    onChange={(e) => setNewHost({...newHost, display_name: e.target.value})}
                    placeholder="John Doe"
                    className="input-glow"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="venue_name">Venue Name</Label>
                  <Input
                    id="venue_name"
                    value={newHost.venue_name}
                    onChange={(e) => setNewHost({...newHost, venue_name: e.target.value})}
                    placeholder="Enter venue name (will create if doesn't exist)"
                    className="input-glow"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={newHost.password}
                    onChange={(e) => setNewHost({...newHost, password: e.target.value})}
                    placeholder="Secure password"
                    className="input-glow"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subscription-level">Subscription Level</Label>
                  <select
                    id="subscription-level"
                    value={newHost.subscription_level}
                    onChange={(e) => setNewHost({...newHost, subscription_level: e.target.value})}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground input-glow"
                  >
                    <option value="basic">Basic - Essential features</option>
                    <option value="regular">Regular - Enhanced features</option>
                    <option value="enterprise">Enterprise - Full customization</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subscription-duration">Subscription Duration (Months)</Label>
                  <select
                    id="subscription-duration"
                    value={newHost.subscription_duration_months}
                    onChange={(e) => setNewHost({...newHost, subscription_duration_months: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground input-glow"
                  >
                    <option value="1">1 Month</option>
                    <option value="3">3 Months</option>
                    <option value="6">6 Months</option>
                    <option value="12">12 Months</option>
                    <option value="24">24 Months</option>
                    <option value="36">36 Months</option>
                  </select>
                </div>
                <div className="md:col-span-3 flex gap-2">
                  <Button onClick={handleCreateHost} className="btn-karaoke">
                    Create Host Account
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowCreateForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Hosts List */}
            <div className="space-y-3">
              {loading ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">Loading hosts...</p>
                </div>
              ) : hosts.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No hosts created yet. Create your first host above.</p>
                </div>
              ) : (
                hosts.map((host) => (
                  <div key={host.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 sm:p-6 border border-border rounded-lg bg-card/50 hover:bg-card/70 transition-colors">
                    <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-primary rounded-full flex items-center justify-center flex-shrink-0">
                        <Users className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-semibold text-base sm:text-lg truncate">{host.display_name}</h4>
                        <p className="text-sm text-muted-foreground truncate">@{host.username}</p>
                        <p className="text-xs text-muted-foreground truncate">{host.venue_name}</p>
                        <p className="text-xs text-green-400 font-mono break-all">Password: {host.password_hash}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            host.subscription_level === 'enterprise'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : host.subscription_level === 'regular'
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-gray-500/20 text-gray-400'
                          }`}>
                            {host.subscription_level === 'enterprise' ? (
                              <>
                                <Crown className="h-3 w-3 mr-1" />
                                Enterprise
                              </>
                            ) : host.subscription_level === 'regular' ? (
                              <>
                                <Star className="h-3 w-3 mr-1" />
                                Regular
                              </>
                            ) : (
                              <>
                                <Star className="h-3 w-3 mr-1" />
                                Basic
                              </>
                            )}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-blue-400">
                            <Calendar className="h-3 w-3 inline mr-1" />
                            Expires: {host.subscription_end_date ? new Date(host.subscription_end_date).toLocaleDateString() : 'N/A'}
                          </span>
                          <span className="text-xs text-green-400">
                            ({host.subscription_duration_months || 12} months)
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">ID: {host.id}</p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 w-full sm:w-auto">
                      <div className="text-center sm:text-right">
                        <div className="flex flex-col gap-1">
                          <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            host.is_active !== false
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {host.is_active !== false ? 'Active' : 'Suspended'}
                          </div>
                          <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            host.subscription_level === 'enterprise'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : host.subscription_level === 'regular'
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-gray-500/20 text-gray-400'
                          }`}>
                            {host.subscription_level === 'enterprise' ? '👑 Enterprise' : host.subscription_level === 'regular' ? '⭐ Regular' : '📊 Basic'}
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">Since {new Date(host.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex flex-col gap-2 w-full sm:min-w-[200px]">
                        {/* Subscription Level */}
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-muted-foreground min-w-[50px]">Level:</label>
                          <select
                            value={host.subscription_level || 'regular'}
                            onChange={(e) => handleUpdateSubscriptionLevel(host.id, e.target.value)}
                            className="text-xs px-2 py-1 rounded border border-border bg-background flex-1"
                          >
                            <option value="basic">Basic</option>
                            <option value="regular">Regular</option>
                            <option value="enterprise">Enterprise</option>
                          </select>
                        </div>

                        {/* Extend Subscription */}
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-muted-foreground min-w-[50px]">Extend:</label>
                          <select
                            onChange={(e) => {
                              if (e.target.value) {
                                handleExtendSubscription(host.id, parseInt(e.target.value));
                                e.target.value = '';
                              }
                            }}
                            className="text-xs px-2 py-1 rounded border border-border bg-background flex-1"
                          >
                            <option value="">+ Months</option>
                            <option value="1">+1 Month</option>
                            <option value="3">+3 Months</option>
                            <option value="6">+6 Months</option>
                            <option value="12">+12 Months</option>
                            <option value="24">+24 Months</option>
                          </select>
                        </div>

                        {/* Status */}
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-muted-foreground min-w-[50px]">Status:</label>
                          <select
                            value={host.is_active !== false ? 'active' : 'inactive'}
                            onChange={(e) => handleToggleHostActivation(host.id, e.target.value === 'inactive')}
                            className="text-xs px-2 py-1 rounded border border-border bg-background flex-1"
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-1 mt-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditHost(host)}
                            className="text-blue-500 hover:bg-blue-500/10 flex-1"
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteHost(host.id)}
                            className="flex-1"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Venues Section */}
        <Card className="card-neon">
          <CardHeader>
            <CardTitle className="text-2xl">Venue Management</CardTitle>
            <CardDescription>View and access all venue singer pages</CardDescription>
          </CardHeader>
          <CardContent>
            {venues.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Music className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No venues created yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {venues.map((venue) => (
                  <div key={venue.id} className="p-4 border border-border rounded-lg bg-card/50">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold">{venue.name}</h4>
                        <p className="text-sm text-muted-foreground">Slug: {venue.slug}</p>
                        <p className="text-xs text-muted-foreground">ID: {venue.id}</p>
                      </div>
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/singer/${venue.slug}`)}
                            className="text-blue-500 hover:bg-blue-500/10"
                          >
                            <Music className="h-4 w-4 mr-1" />
                            Singer Page
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleResetSongDatabase(venue.id, venue.name)}
                            className="text-orange-500 hover:bg-orange-500/10"
                          >
                            🗑️ Reset Songs
                          </Button>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteVenue(venue.id)}
                          className="w-full"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete Venue
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Host Dialog */}
        {showEditForm && editingHost && (
          <Dialog open={showEditForm} onOpenChange={setShowEditForm}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Edit Host Details</DialogTitle>
                <DialogDescription>
                  Update the host information and credentials
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-username">Username</Label>
                  <Input
                    id="edit-username"
                    value={editingHost.username}
                    onChange={(e) => setEditingHost({...editingHost, username: e.target.value})}
                    placeholder="Enter username"
                    className="input-glow"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-display-name">Display Name</Label>
                  <Input
                    id="edit-display-name"
                    value={editingHost.display_name}
                    onChange={(e) => setEditingHost({...editingHost, display_name: e.target.value})}
                    placeholder="Enter display name"
                    className="input-glow"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-password">Password</Label>
                  <Input
                    id="edit-password"
                    type="password"
                    value={editingHost.password_hash || ''}
                    onChange={(e) => setEditingHost({...editingHost, password_hash: e.target.value})}
                    placeholder="Enter new password (leave blank to keep current)"
                    className="input-glow"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleUpdateHost}
                    className="flex-1 btn-karaoke"
                  >
                    Update Host
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowEditForm(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default SuperAdmin;